// A DIALOG KEEPS WHAT IT SHOWED UNTIL IT HAS GONE (QA wave B, 2026-09-25,
// B-04 and B-06; `docs/decisions.md` D-169).
//
// Fluent keeps a dialog's surface mounted through its exit transition, so a
// dialog goes on rendering for a moment after `open` turns false. Every editor
// on References, Rules and Assignments derives its body from state that the
// same click clears or refreshes: the row it was opened on (`row === 'new'`
// flips an Add title to Edit and grows a Status field), the list it reloads
// (a just-saved ID finds itself and paints "already exists"), or the target it
// writes through (Assignments' `target === null` read as "You do not have
// permission"). Each fix so far latched ONE of those values — `lastRow` in
// `CodeDialog`, `shown` in `HoldControl` — and the next derived value leaked.
//
// This latches the whole body instead. While `open` is true it renders its
// children and remembers them; once `open` goes false it renders the last
// children it saw open, so nothing the closing click changed can reach the
// screen. The words shown are frozen, not delayed: when the dialog reopens it
// renders live children again at once.
//
// SINCE D-239 (QA 2026-09-30, C1-10) no dialog in the app keeps its surface
// through an exit animation: `AppDialog` switches the surface motion off, so a
// closed dialog unmounts in the same render and the latched body is never
// drawn. It is kept because it is inert: while `open` is true it renders its
// children unchanged.
import { useEffect, useState, type ReactNode } from 'react'

export function FrozenWhileClosed({ open, children }: { open: boolean; children: ReactNode }) {
  const [last, setLast] = useState<ReactNode>(children)
  useEffect(() => {
    // Remembered after each OPEN render only. The re-render this causes hands
    // the same `children` object back, which React does not reconcile again.
    if (open) setLast(children)
  }, [open, children])
  return <>{open ? children : last}</>
}
